/*
 * usePushNotifications — one-tap opt-in to Firebase Cloud Messaging.
 *
 * Usage (driver):
 *   const push = usePushNotifications({ beaconKey });
 * Usage (rider):
 *   const push = usePushNotifications({ riderToken });
 *
 * Flow:
 *   1. Fetch public Firebase config from /api/rider/push/config
 *   2. Init firebase/app once, get messaging()
 *   3. Prompt user for Notification.permission
 *   4. Register /firebase-messaging-sw.js
 *   5. getToken → POST to /api/rider/push/register-token (driver)
 *      or /api/rider/push/register-rider-token (rider)
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { initializeApp, getApps } from "firebase/app";
import { getMessaging, getToken, isSupported } from "firebase/messaging";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/rider/push`;

let _cfg = null;
let _msg = null;

/*
 * Apple requires an iOS Safari page to be installed to the Home Screen
 * (standalone display mode) before Web Push works at all — confirmed still
 * true as of 2026 (webkit.org/blog/13878). On a regular Safari tab, some iOS
 * versions still expose `Notification`/`PushManager` globals but throw
 * opaque, uncatchable errors deep inside the Push subscription flow when
 * actually used (root cause of the "Uncaught runtime errors / Script error."
 * crash reported on Safari). So we never call into Firebase Messaging at
 * all on iOS Safari unless the page is already running standalone.
 */
function isIOSNonStandaloneSafari() {
  if (typeof navigator === "undefined" || typeof window === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  if (!isIOS) return false;
  const standalone = window.navigator.standalone === true ||
    (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches);
  return !standalone;
}

async function initMessaging() {
  if (_msg) return _msg;
  if (!(await isSupported().catch(() => false))) return null;
  if (!_cfg) {
    const r = await axios.get(`${API}/config`);
    if (!r.data?.enabled) return null;
    _cfg = r.data;
  }
  const firebaseConfig = {
    apiKey: _cfg.web_api_key,
    authDomain: _cfg.auth_domain,
    projectId: _cfg.project_id,
    messagingSenderId: _cfg.messaging_sender_id,
    appId: _cfg.app_id,
  };
  const app = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);
  _msg = getMessaging(app);
  return _msg;
}

export default function usePushNotifications({ beaconKey, riderToken, cpdDriverToken } = {}) {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );
  const [enabling, setEnabling] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    if (isIOSNonStandaloneSafari()) {
      // Never probe Firebase here — Apple blocks push outright in this mode.
      setSupported(false);
      // Kill any zombie SW registration left over from before this guard
      // existed — it re-runs importScripts() to gstatic.com on every visit
      // and is the actual source of the opaque "Script error." crash Safari
      // reports for cross-worker exceptions, independent of anything else
      // on this page.
      if (typeof navigator !== "undefined" && navigator.serviceWorker) {
        navigator.serviceWorker.getRegistrations()
          .then((regs) => regs.forEach((r) => {
            const url = r.active?.scriptURL || r.installing?.scriptURL || r.waiting?.scriptURL || "";
            if (url.includes("firebase-messaging-sw.js")) r.unregister().catch(() => {});
          }))
          .catch(() => {});
      }
      return () => { mounted = false; };
    }
    isSupported()
      .then((ok) => mounted && setSupported(!!ok))
      .catch(() => mounted && setSupported(false));
    return () => { mounted = false; };
  }, []);

  const enable = useCallback(async () => {
    if (isIOSNonStandaloneSafari()) {
      setError("Add MedexOVA to your Home Screen first, then turn on notifications.");
      return false;
    }
    if (!beaconKey && !riderToken && !cpdDriverToken) { setError("Missing identity"); return false; }
    setEnabling(true);
    setError(null);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") { setEnabling(false); return false; }
      const messaging = await initMessaging();
      if (!messaging) { setError("Push not configured"); setEnabling(false); return false; }
      const reg = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
      const token = await getToken(messaging, {
        vapidKey: _cfg?.vapid_key,
        serviceWorkerRegistration: reg,
      });
      if (!token) { setError("No FCM token"); setEnabling(false); return false; }
      if (beaconKey) {
        await axios.post(`${API}/register-token`, {
          beacon_key: beaconKey,
          fcm_token: token,
          platform: "web",
        });
      } else if (cpdDriverToken) {
        await axios.post(`${API}/register-cpd-driver-token`, {
          driver_token: cpdDriverToken,
          fcm_token: token,
          platform: "web",
        });
      } else {
        await axios.post(`${API}/register-rider-token`, {
          rider_token: riderToken,
          fcm_token: token,
          platform: "web",
        });
      }
      setEnabling(false);
      return true;
    } catch (e) {
      setError(e?.message || "Push setup failed");
      setEnabling(false);
      return false;
    }
  }, [beaconKey, riderToken, cpdDriverToken]);

  return { supported, permission, enabling, error, enable };
}
